import { useAppStore } from '@/stores/app-store';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Per-user, per-app localStorage cache keys.
 *
 * The `ua_cache_*` app caches (org id, CRM user id, trial start) used to be
 * global — one key shared by every account and every installed app. Signing
 * out only cleared `ua_cache_user*`, so the next login seeded its module
 * context from the PREVIOUS account's org id (use-app-init reads the cache
 * before the real org resolves). Settings + trial then loaded against the
 * wrong org and showed the wrong trial period until a manual reload.
 *
 * Scoping the key by `{uid}:{appCode}` makes a mismatched account/app simply
 * miss the cache instead of reading someone else's value — no cleanup path
 * can be forgotten. Also fixes switching between two apps in the same
 * account, which had the same collision.
 */

/**
 * App-identity + trial caches. These survive `clearProxyCache()` (which runs
 * on reauthorize / incoming-sync changes) because they aren't derived from
 * TPA credentials — re-fetching them on every reauthorize would undo the
 * fast-start seeding they exist for.
 */
export const PRESERVED_CACHE_BASES = [
  'ua_cache_appOrgId',
  'ua_cache_appUniqueId',
  'ua_cache_trial_start',
] as const;

/**
 * `ua_cache_*` caches holding API/permission data. `clearProxyCache()` is
 * expected to DROP these — they're stale the moment credentials change — so
 * they must NOT be reported as preserved keys.
 */
export const VOLATILE_CACHE_BASES = [
  'ua_cache_team_permissions',
  'ua_cache_notification_routing',
  'ua_cache_existingTwilioServiceID',
  'ua_cache_existingTwilioServiceURL',
  'ua_cache_existingTwilioServiceFriendlyName',
  'ua_cache_hubspotUsersCount',
  'ua_cache_sharedAdminOrg',
] as const;

/**
 * Scoped keys that don't carry the `ua_cache_` prefix, so `clearProxyCache()`
 * never sweeps them either way. Listed here purely so they get scoped and
 * legacy-purged like the rest.
 */
export const OTHER_SCOPED_BASES = [
  'ua_monday_subdomain',
  'ua_onboarding_nstep',
  'whatcetra_hubspot_custom_auth_done',
] as const;

/** Every base that is scoped by {uid, appCode}. */
export const SCOPED_CACHE_BASES = [
  ...PRESERVED_CACHE_BASES,
  ...VOLATILE_CACHE_BASES,
  ...OTHER_SCOPED_BASES,
] as const;

export type ScopedCacheBase = (typeof SCOPED_CACHE_BASES)[number];

/**
 * Bases the same-origin legacy app also reads, so writes must keep an
 * unscoped mirror and the legacy purge must leave them alone. Verified
 * against public/legacy (and public/js):
 *   ua_cache_appOrgId / ua_cache_appUniqueId  → firebase-things.js:167-168
 *   whatcetra_hubspot_custom_auth_done        → saas/hubspot-crm-things.js:187
 *   ua_cache_existingTwilioService{ID,URL,FriendlyName}
 *                                             → tpa/twilio-things.js:1674-1677
 *   ua_cache_hubspotUsersCount                → saas/hubspot-crm-things.js:277
 * Keys absent from legacy (trial_start, monday_subdomain, team_permissions,
 * notification_routing, onboarding_nstep) are intentionally NOT mirrored.
 */
const LEGACY_MIRRORED_BASES: ReadonlySet<string> = new Set([
  'ua_cache_appOrgId',
  'ua_cache_appUniqueId',
  'whatcetra_hubspot_custom_auth_done',
  'ua_cache_existingTwilioServiceID',
  'ua_cache_existingTwilioServiceURL',
  'ua_cache_existingTwilioServiceFriendlyName',
  'ua_cache_hubspotUsersCount',
]);

interface ScopeOverrides {
  /** Defaults to the signed-in user's uid. */
  uid?: string | null;
  /** Defaults to `appStore.extensionName` (e.g. "calendlyforzohocrm"). */
  appCode?: string | null;
}

/**
 * Resolve the scope, preferring explicit overrides. Early in app init the
 * stores aren't populated yet, so callers that already know the app code
 * pass it in rather than relying on the store.
 */
function resolveScope(overrides?: ScopeOverrides): { uid: string; appCode: string } | null {
  let uid = overrides?.uid ?? null;
  let appCode = overrides?.appCode ?? null;
  try {
    if (!uid) uid = useAuthStore.getState().user?.uid ?? null;
    if (!appCode) appCode = useAppStore.getState().extensionName ?? null;
  } catch {
    // Store access can throw during very early module evaluation.
  }
  if (!uid || !appCode) return null;
  return { uid, appCode };
}

/** `ua_cache_appOrgId` → `ua_cache_appOrgId:{uid}:{appCode}`. */
export function scopedCacheKey(
  base: ScopedCacheBase,
  overrides?: ScopeOverrides,
): string | null {
  const scope = resolveScope(overrides);
  if (!scope) return null;
  return `${base}:${scope.uid}:${scope.appCode}`;
}

/**
 * Read a scoped cache value. Returns null when the scope can't be resolved —
 * callers must treat that as a cache miss and fall back to a real lookup,
 * never to the legacy unscoped key (that's the cross-account leak).
 */
export function readScopedCache(
  base: ScopedCacheBase,
  overrides?: ScopeOverrides,
): string | null {
  const key = scopedCacheKey(base, overrides);
  if (!key) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Write a scoped cache value.
 *
 * Also mirrors to the UNSCOPED key for bases the legacy app reads. Both apps
 * are served from the same origin (React at `/`, legacy at
 * `/legacy/app.html` — linked from Settings → "Access old version"), so they
 * share one localStorage and have always used these plain key names as the
 * handoff. Writing only the scoped key starves legacy: it reads the plain
 * name, gets null, and e.g. boots with `UA_DESK_ORG_ID = null`
 * (public/legacy/js/firebase-things.js:167).
 *
 * The mirror is write-only from React's side — reads still go exclusively
 * through the scoped key, so a previous account's value can never leak back
 * into this app. Legacy re-resolves org/user from the CRM SDK on boot, so a
 * slightly stale mirror there is its pre-existing behaviour, not a new risk.
 */
export function writeScopedCache(
  base: ScopedCacheBase,
  value: string | null | undefined,
  overrides?: ScopeOverrides,
): void {
  const write = (k: string) => {
    try {
      if (value == null || value === '') localStorage.removeItem(k);
      else localStorage.setItem(k, value);
    } catch {
      // localStorage access can fail (private mode / quota)
    }
  };

  const key = scopedCacheKey(base, overrides);
  if (key) write(key);
  // Mirror even when the scope is unresolved — legacy still needs the value.
  if (LEGACY_MIRRORED_BASES.has(base)) write(base);
}

/**
 * Drop the pre-scoping global keys so a stale org id from another account
 * doesn't sit in storage forever. Skips the bases the legacy app reads —
 * deleting those is exactly what breaks it. Safe to call repeatedly.
 */
export function purgeLegacyUnscopedCaches(): void {
  try {
    for (const base of SCOPED_CACHE_BASES) {
      if (LEGACY_MIRRORED_BASES.has(base)) continue;
      localStorage.removeItem(base);
    }
  } catch {
    // localStorage access can fail
  }
}

/**
 * True for any scoped variant (`base` or `base:{uid}:{appCode}`) of a cache
 * that `clearProxyCache()` must keep. Deliberately only the PRESERVED set —
 * the volatile ones still get swept, exactly as before scoping existed.
 */
export function isPreservedCacheKey(key: string): boolean {
  return PRESERVED_CACHE_BASES.some((base) => key === base || key.startsWith(`${base}:`));
}
